import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { FullstoryService } from '@shared-lib/services/fullstory.service';
import { AppState } from '@shared/models/app-state.model';
import { AuthService } from '@shared/services/auth.service';
import { getClientUserSuccess } from '@shared/store/client-user/client-user.actions';
import { selectClientUserId } from '@shared/store/client-user/client-user.selectors';
import { filter, tap, withLatestFrom } from 'rxjs/operators';

/**
 * Creates company identifier string, used in Fullstory and Google analytics
 * @param companyId
 * @param isAdmin
 */
const companyIdentifier = (companyId: string, isAdmin: boolean): string => {
  if (isAdmin) {
    return '[sys-admin]';
  }
  if (companyId == null) {
    return 'N/A';
  }
  return `[${companyId}]`;
};

@Injectable()
export class FullstoryEffect {
  private readonly actions = inject(Actions);
  private readonly store: Store<AppState> = inject(Store<AppState>);
  private readonly authService = inject(AuthService);
  private readonly fullstoryService = inject(FullstoryService);

  identifyUser = createEffect(
    () =>
      this.actions.pipe(
        ofType(getClientUserSuccess),
        withLatestFrom(this.store.select(selectClientUserId)),
        filter(([action, userId]) => action.clientUser && !!userId),
        tap(([action, userId]) => {
          this.fullstoryService.identify(userId, {
            displayName: `${action.clientUser.firstName} ${action.clientUser.lastName}`,
            email: action.clientUser.email,
            companyIdentifier: companyIdentifier(action.clientUser.companyId, this.authService.isImpersonated),
          });
        }),
      ),
    { dispatch: false },
  );
}
